import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import Button from '../../components/ui/Button';

const AdesioneCommercianti = () => {
  const [formData, setFormData] = useState({
    nomeAttivita: '',
    indirizzoAttivita: '',
    email: '',
    telefono: '',
    referente: '',
    settori: [],
    altroSettore: '',
    privacy: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const settoriOptions = [
    'Abbigliamento',
    'Accessori',
    'Acconciatura',
    'Benessere',
    'Calzature',
    'Farmacia',
    'Food Bar',
    'Food Gelateria',
    'Food Pasticceria',
    'Food Ristorazione',
    'Food Vendita',
    'Gioielleria',
    'Merceria',
    'Oggettistica',
    'Tabacchi',
    'Altri'
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSettoreChange = (settore, checked) => {
    setFormData(prev => {
      const newSettori = checked
        ? [...prev?.settori, settore]
        : prev?.settori?.filter(s => s !== settore);
      return { ...prev, settori: newSettori };
    });

    if (errors?.settori) {
      setErrors(prev => ({ ...prev, settori: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.nomeAttivita?.trim()) {
      newErrors.nomeAttivita = 'Il nome attività è obbligatorio';
    }

    if (!formData?.indirizzoAttivita?.trim()) {
      newErrors.indirizzoAttivita = "L'indirizzo attività è obbligatorio";
    }

    if (!formData?.email?.trim()) {
      newErrors.email = "L'email è obbligatoria";
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Inserisci un indirizzo email valido';
    }

    if (!formData?.telefono?.trim()) {
      newErrors.telefono = 'Il telefono è obbligatorio';
    }

    if (!formData?.referente?.trim()) {
      newErrors.referente = 'Il referente è obbligatorio';
    }

    if (formData?.settori?.length === 0) {
      newErrors.settori = 'Seleziona almeno un settore';
    }

    if (formData?.settori?.includes('Altri') && !formData?.altroSettore?.trim()) {
      newErrors.altroSettore = 'Specifica il settore';
    }

    if (!formData?.privacy) {
      newErrors.privacy = 'Devi accettare il trattamento dei dati';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-merchant-interest`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result?.error || 'Errore durante l\'invio');
      }

      setShowSuccess(true);

    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage(error?.message || 'Errore durante l\'invio. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <>
        <Helmet>
          <title>Grazie - Manifestazione di Interesse</title>
        </Helmet>

        <Header />

        <div className="min-h-screen bg-white flex items-center justify-center pt-20">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="bg-green-50 border border-green-200 rounded-xl shadow-lg p-12">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Grazie per aver manifestato interesse
              </h1>
              <p className="text-lg text-gray-700">
                Sarai ricontattato per il prossimo incontro operativo.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Manifestazione di Interesse – Progetto Cliccami</title>
        <meta 
          name="description" 
          content="Compila il form per manifestare il tuo interesse al Progetto Cliccami" 
        />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-background pt-20 pb-24">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Manifestazione di Interesse – Progetto Cliccami
            </h1>
            <p className="text-gray-600 mb-8">
              Compila il form per aderire al progetto
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nome Attività */}
              <Input
                label="Nome Attività"
                type="text"
                value={formData?.nomeAttivita}
                onChange={(e) => handleInputChange('nomeAttivita', e?.target?.value)}
                error={errors?.nomeAttivita}
                required
                placeholder="Inserisci il nome dell'attività"
              />

              {/* Indirizzo Attività */}
              <Input
                label="Indirizzo Attività"
                type="text"
                value={formData?.indirizzoAttivita}
                onChange={(e) => handleInputChange('indirizzoAttivita', e?.target?.value)}
                error={errors?.indirizzoAttivita}
                required
                placeholder="Inserisci l'indirizzo dell'attività"
              />

              {/* Email */}
              <Input
                label="Mail"
                type="email"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                required
                placeholder="esempio@email.com"
              />

              {/* Telefono */}
              <Input
                label="Telefono"
                type="text"
                value={formData?.telefono}
                onChange={(e) => handleInputChange('telefono', e?.target?.value)}
                error={errors?.telefono}
                required
                placeholder="Inserisci il numero di telefono"
              />

              {/* Referente */}
              <Input
                label="Referente per il Progetto Cliccami"
                type="text"
                value={formData?.referente}
                onChange={(e) => handleInputChange('referente', e?.target?.value)}
                error={errors?.referente}
                required
                placeholder="Nome e cognome del referente"
              />

              {/* Settore - Multiple Checkboxes */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  Settore <span className="text-destructive">*</span>
                </label>
                <div className="space-y-2 border border-gray-200 rounded-md p-4 max-h-64 overflow-y-auto">
                  {settoriOptions?.map((settore) => (
                    <Checkbox
                      key={settore}
                      label={settore}
                      checked={formData?.settori?.includes(settore)}
                      onChange={(e) => handleSettoreChange(settore, e?.target?.checked)}
                    />
                  ))}
                </div>
                {errors?.settori && (
                  <p className="text-sm text-destructive">{errors?.settori}</p>
                )}
              </div>

              {/* Altri Settore - Conditional Field */}
              {formData?.settori?.includes('Altri') && (
                <Input
                  label="Specifica Settore"
                  type="text"
                  value={formData?.altroSettore}
                  onChange={(e) => handleInputChange('altroSettore', e?.target?.value)}
                  error={errors?.altroSettore}
                  required
                  placeholder="Specifica il settore"
                />
              )}

              {/* Privacy Checkbox */}
              <Checkbox
                label="Acconsento al trattamento dei dati per finalità organizzative del progetto Cliccami.net"
                checked={formData?.privacy}
                onChange={(e) => handleInputChange('privacy', e?.target?.checked)}
                error={errors?.privacy}
                required
              />

              {/* Error Message */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
                size="lg"
              >
                {isSubmitting ? 'Invio in corso...' : 'INVIA'}
              </Button>
            </form>
          </div>
        </div>
      </main>
    </>
  );
};

export default AdesioneCommercianti;